import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { generateUUID } from "../../../../common/utils/uuid.ts";
import { removeItem } from "../../../../common/utils/array.ts";
import { DisplayableToast } from "./types.ts";

interface ToastData {
  toastElement: DisplayableToast;
  id: string;
  targetId?: string;
}

type ToastOptions = Partial<{
  id: string;
  targetId?: string;
}>;

interface ToastContextType {
  toasts: ToastData[];
  showToast: (toast: DisplayableToast, opts?: ToastOptions) => string;
  closeToast: (toastId: string) => void;
}

const defaultContext = {
  toasts: [],
  showToast: () => "",
  closeToast: () => null,
};

export const ToastContext =
  React.createContext<ToastContextType>(defaultContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toastList, setToastList] = useState<string[]>([]);
  const [toastData, setToastData] = useState<Record<string, ToastData>>({});

  /**
   * Also updates an existing toast
   */
  const showToast = useCallback(
    (toast: DisplayableToast, opts: ToastOptions = {}) => {
      const id = opts.id ?? generateUUID();
      // This timeout addresses an accessibility timing issue when dialogs
      // are closed and button focus is restored to the opener
      setTimeout(() => {
        setToastData((oldData) => ({
          ...oldData,
          [id]: { toastElement: toast, id, targetId: opts.targetId },
        }));
        setToastList((oldList) => {
          if (!oldList.includes(id)) {
            return [id, ...oldList];
          }
          return oldList;
        });
      }, 250);
      return id;
    },
    []
  );

  const closeToast = useCallback((id: string) => {
    setToastList((oldList) => removeItem(oldList, id));
    setToastData((oldData) => {
      const clonedData = { ...oldData };
      delete clonedData[id];
      return clonedData;
    });
  }, []);

  const memoizedContext = useMemo(
    () => ({
      toasts: toastList.map((id) => toastData[id]),
      showToast,
      closeToast,
    }),
    [toastList, toastData, showToast, closeToast]
  );

  return (
    <ToastContext.Provider value={memoizedContext}>
      {children}
    </ToastContext.Provider>
  );
}
